import React, { useCallback, useContext, useMemo } from 'react'
import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import styled from 'styled-components'
import { isHotelPackage } from 'lib/offer/offerTypes'
import HeightFillingImage from 'components/Common/HeightFillingImage'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useHasMounted from 'hooks/useHasMounted'
import { carouselImageImpressionEvent } from 'analytics/snowplow/events'
import OfferPageRoomGallery from 'components/OfferPage/OfferPageRoomGallery'
import { ImageChangeHandler } from 'LuxKit/Carousel/BaseImageCarousel'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import * as Analytics from 'analytics/analytics'
import Clickable from 'components/Common/Clickable'
import ImageGalleryLightbox from 'LuxKit/Carousel/ImageGalleryLightbox'
import ModalContext from 'contexts/ModalContext'
import uuidV4 from 'lib/string/uuidV4Utils'
import { accommodationIndividualRoomTileGridChildEnforcer } from './AccommodationIndividualRoomRoomTileGridBase'

export const AccommodationIndividualRoomTileImageContainer = styled.div`
  ${accommodationIndividualRoomTileGridChildEnforcer}
  display: grid; /* For some reason the image gallery needs this? */
`

const DesktopBreakpoint = styled(CSSBreakpoint)`
  height: 100%;
`

const IMAGE_PROPS: ImageParams = {
  mobileWidth: '80px',
  mobileAspectRatio: '80:76',
  tabletWidth: '120px',
  tabletAspectRatio: '80:76',
  desktopWidth: '400px',
  desktopAspectRatio: 'auto',
  quality: 'good',
}

interface Props {
  roomPackage?: App.HotelPackage | App.BedbankPackage
  offer: App.HotelOffer | App.BedbankOffer
  /** @default true */
  showBookmark?: boolean
}

function AccommodationIndividualRoomTileImage(props: Props) {
  const {
    roomPackage,
    offer,
    showBookmark = true,
  } = props

  const showModal = useContext(ModalContext)

  const room = isHotelPackage(roomPackage) ? roomPackage.roomType : roomPackage
  const roomTypeId = isHotelPackage(roomPackage) ? roomPackage.roomType.id : roomPackage?.roomId

  const listId = useMemo(() => uuidV4(), [])

  const handleImageChange = useCallback<ImageChangeHandler>((index, image) => {
    if (offer && image) {
      Analytics.trackEvent(
        carouselImageImpressionEvent(
          image,
          index + 1,
          'offer-package-gallery',
          listId,
          offer,
          roomTypeId,
        ),
      )
    }
  }, [offer, roomTypeId, listId])

  const isDesktop = useScreenSizeUp('desktop')
  const isMounted = useHasMounted()
  const showCarousel = isDesktop && isMounted

  const imageId = room?.images[0]?.id

  const showImageLightbox = useCallback(() => {
    showModal(<ImageGalleryLightbox
      images={room!.images}
      defaultIndex={0}
    />)
  }, [room, showModal])

  if (!imageId) {
    return null
  }

  return <AccommodationIndividualRoomTileImageContainer>
    {showCarousel && <OfferPageRoomGallery
      room={room}
      imageParams={IMAGE_PROPS}
      onImageChange={handleImageChange}
      topRight={showBookmark && <CSSBreakpoint max="tablet">
        <TripGuard>
          <BookmarkButton
            withLabel="tablet"
            offer={offer}
            roomTypeId={roomTypeId}
          />
        </TripGuard>
      </CSSBreakpoint>}
    />}
    {!showCarousel && <Clickable onClick={showImageLightbox}>
      <CSSBreakpoint max="tablet">
        <ResponsiveImage
          {...IMAGE_PROPS}
          id={imageId}
          alt={room.images[0].title}
        />
      </CSSBreakpoint>
      <DesktopBreakpoint min="desktop">
        <HeightFillingImage
          {...IMAGE_PROPS}
          id={imageId}
          alt={room.images[0].title}
        />
      </DesktopBreakpoint>
    </Clickable>}
  </AccommodationIndividualRoomTileImageContainer>
}

export default AccommodationIndividualRoomTileImage
